import { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../../../context/UsuarioContext";
import useErroPadrao from "../../../hooks/useErroPadrao";
import useFlashMessage from "../../../hooks/useFlashMessages";
import useEquipe from "../../../hooks/useEquipe";
import { CHAVE_TOKEN } from "../../../utils/formata";
import { SECOES } from "../../../utils/secoes";

const FORM_VAZIO = {
    nome: "", email: "", login: "", senha: "", senhaConfirmacao: "",
    admin: false, permissoes: [],
};

const estiloChip = {
    background: "rgba(212, 175, 55, 0.12)",
    border: "1px solid rgba(212, 175, 55, 0.4)",
    color: "#d4af37",
    borderRadius: 20,
    padding: "2px 10px",
    fontSize: "0.75em",
    fontWeight: 600,
    whiteSpace: "nowrap",
};

export default function EquipePainel() {
    const { usuario: usuarioLogado } = useContext(Context);
    const [usuarios, setUsuarios] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [form, setForm] = useState(null); // null = fechado; {id} presente = editando
    const [salvando, setSalvando] = useState(false);
    const { listarUsuarios, criarUsuario, atualizarUsuario, removerUsuario } = useEquipe();
    const { setErroPadrao } = useErroPadrao();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigate();

    const carregar = useCallback(() => {
        setCarregando(true);
        listarUsuarios()
            .then((lista) => setUsuarios(lista || []))
            .catch((error) => setErroPadrao(error))
            .finally(() => setCarregando(false));
    }, [listarUsuarios, setErroPadrao]);

    useEffect(() => {
        if (!localStorage.getItem(CHAVE_TOKEN)) {
            navigate("/login");
            return;
        }
        if (usuarioLogado?.id && usuarioLogado.admin) carregar();
    }, [carregar, navigate, usuarioLogado?.id, usuarioLogado?.admin]);

    const editando = !!form?.id;

    function abrirCriacao() {
        setForm({ ...FORM_VAZIO });
    }

    function abrirEdicao(u) {
        setForm({
            id: u.id,
            nome: u.nome || "",
            email: u.email || "",
            login: u.login || "",
            senha: "",
            senhaConfirmacao: "",
            admin: !!u.admin,
            permissoes: u.permissoes || [],
        });
    }

    function togglePermissao(chave) {
        setForm((f) => ({
            ...f,
            permissoes: f.permissoes.includes(chave)
                ? f.permissoes.filter((c) => c !== chave)
                : [...f.permissoes, chave],
        }));
    }

    function handleSubmit(e) {
        e.preventDefault();
        if (!editando && form.senha !== form.senhaConfirmacao) {
            setFlashMessage("A senha e a confirmação precisam ser iguais.", "error");
            return;
        }
        setSalvando(true);
        const permissoes = form.admin ? [] : form.permissoes;
        const promessa = editando
            ? atualizarUsuario(form.id, {
                nome: form.nome,
                permissoes,
                admin: form.admin,
                ...(form.senha ? { senha: form.senha } : {}),
            })
            : criarUsuario({
                // Campos de texto vão em array de 1 posição — padrão do backend (igual ao login)
                nome: [form.nome],
                email: [form.email],
                login: [form.login],
                senha: [form.senha],
                senhaConfirmacao: [form.senhaConfirmacao],
                permissoes,
                admin: form.admin,
            });
        promessa
            .then((resp) => {
                setFlashMessage(resp.message || (editando ? "Usuário atualizado!" : "Usuário criado!"), "success");
                setForm(null);
                carregar();
            })
            .catch((error) => setErroPadrao(error))
            .finally(() => setSalvando(false));
    }

    function handleExcluir(u) {
        if (!window.confirm(`Excluir o usuário "${u.nome}" de vez?\n\nEssa ação não tem volta.`)) return;
        removerUsuario(u.id)
            .then((resp) => {
                setFlashMessage(resp.message || "Usuário excluído!", "success");
                carregar();
            })
            .catch((error) => setErroPadrao(error));
    }

    // Usuário do contexto ainda carregando — nunca piscar conteúdo proibido
    if (!usuarioLogado?.id) {
        return (
            <div className="pagina" style={{ textAlign: "center", padding: "3em" }}>
                <span className="spinner-border" style={{ color: "#d4af37" }}></span>
            </div>
        );
    }

    if (!usuarioLogado.admin) {
        return (
            <div className="pagina">
                <div className="he-card" style={{ maxWidth: 480, margin: "3em auto", textAlign: "center" }}>
                    <i className="bi bi-shield-lock he-dourado" style={{ fontSize: "2.5em" }}></i>
                    <h4 className="he-titulo" style={{ marginTop: "0.5em" }}>Só o administrador acessa a Equipe</h4>
                    <p style={{ color: "#9aa5b5", fontSize: "0.9em" }}>
                        Fale com quem administra o painel se precisar de acesso.
                    </p>
                    <button className="btn btn-he" onClick={() => navigate("/")}>
                        <i className="bi bi-arrow-left"></i> Voltar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="pagina">
            <div className="cabecalho-pagina">
                <h2 className="he-titulo" style={{ margin: 0 }}>Sua <span className="he-dourado">Equipe</span></h2>
                {!form &&
                    <button className="btn btn-he" onClick={abrirCriacao}>
                        <i className="bi bi-person-plus"></i> Adicionar usuário
                    </button>
                }
            </div>
            <p style={{ color: "#9aa5b5", fontSize: "0.9em", marginTop: 4 }}>
                Cada pessoa enxerga só as seções que você liberar. Sem nenhuma seção marcada, ela vê tudo.
            </p>

            {form &&
                <form onSubmit={handleSubmit} className="he-card form-mobile" style={{ border: "1px solid #d4af37", margin: "1em 0", display: "flex", flexDirection: "column", gap: 14 }}>
                    <h5 className="he-titulo he-dourado" style={{ margin: 0 }}>
                        <i className={editando ? "bi bi-pencil-square" : "bi bi-person-plus"}></i>{" "}
                        {editando ? `Editar — ${form.nome || form.login}` : "Novo usuário"}
                    </h5>

                    <div className="row">
                        <div className="col-md-5 campo-form">
                            <label className="form-label" style={{ fontSize: "0.8em" }}>Nome</label>
                            <input className="form-control form-control-sm" value={form.nome}
                                onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
                        </div>
                        <div className="col-md-4 campo-form">
                            <label className="form-label" style={{ fontSize: "0.8em" }}>E-mail</label>
                            <input className="form-control form-control-sm" type="email" value={form.email} disabled={editando}
                                onChange={(e) => setForm({ ...form, email: e.target.value })} required={!editando} />
                        </div>
                        <div className="col-md-3 campo-form">
                            <label className="form-label" style={{ fontSize: "0.8em" }}>Login</label>
                            <input className="form-control form-control-sm" value={form.login} disabled={editando}
                                onChange={(e) => setForm({ ...form, login: e.target.value })} required={!editando} />
                        </div>
                        <div className="col-md-6 campo-form">
                            <label className="form-label" style={{ fontSize: "0.8em" }}>
                                {editando ? "Nova senha (deixe em branco pra manter)" : "Senha"}
                            </label>
                            <input className="form-control form-control-sm" type="password" value={form.senha}
                                autoComplete="new-password"
                                onChange={(e) => setForm({ ...form, senha: e.target.value })} required={!editando} />
                        </div>
                        {!editando &&
                            <div className="col-md-6 campo-form">
                                <label className="form-label" style={{ fontSize: "0.8em" }}>Confirmar senha</label>
                                <input className="form-control form-control-sm" type="password" value={form.senhaConfirmacao}
                                    autoComplete="new-password"
                                    onChange={(e) => setForm({ ...form, senhaConfirmacao: e.target.value })} required />
                            </div>
                        }
                    </div>

                    {/* Switch Admin — a linha inteira é o alvo de toque */}
                    <div
                        onClick={() => setForm({ ...form, admin: !form.admin })}
                        title={form.admin ? "Clique para tirar o acesso de administrador" : "Clique para tornar administrador"}
                        style={{ display: "flex", alignItems: "center", gap: 14, minHeight: 44, cursor: "pointer", userSelect: "none" }}>
                        <div style={{
                            width: 58, height: 30, borderRadius: 20, position: "relative", flexShrink: 0,
                            background: form.admin ? "linear-gradient(135deg, #d4af37 0%, #b8952e 100%)" : "#3a4a63",
                            boxShadow: form.admin ? "0 0 12px rgba(212, 175, 55, 0.6)" : "none",
                            transition: "background 0.25s, box-shadow 0.25s",
                        }}>
                            <div style={{
                                position: "absolute", top: 4, left: form.admin ? 32 : 4,
                                width: 22, height: 22, borderRadius: "50%", background: "#fff",
                                transition: "left 0.25s",
                            }}></div>
                        </div>
                        <span style={{ fontWeight: 700, color: form.admin ? "#d4af37" : "#9aa5b5", minWidth: 0 }}>
                            <i className="bi bi-shield-check"></i> Administrador {form.admin ? "— vê tudo e gerencia a equipe" : ""}
                        </span>
                    </div>

                    {/* Seleção de seções — chips que acendem em dourado ao tocar */}
                    <div>
                        <label className="form-label" style={{ fontSize: "0.8em" }}>Seções que este usuário enxerga</label>
                        <div style={{
                            display: "flex", flexWrap: "wrap", gap: 8,
                            background: "rgba(13, 27, 42, 0.5)",
                            border: "1px solid rgba(255, 255, 255, 0.08)",
                            borderRadius: 10,
                            padding: "0.8em",
                            opacity: form.admin ? 0.5 : 1,
                        }}>
                            {SECOES.map((s) => {
                                const ativo = form.admin || form.permissoes.includes(s.chave);
                                return (
                                    <button
                                        type="button"
                                        key={s.chave}
                                        disabled={form.admin}
                                        onClick={() => togglePermissao(s.chave)}
                                        style={{
                                            display: "inline-flex", alignItems: "center", gap: 7,
                                            minHeight: 40, padding: "0 14px", borderRadius: 999,
                                            border: `1px solid ${ativo ? "transparent" : "rgba(212, 175, 55, 0.35)"}`,
                                            background: ativo
                                                ? "linear-gradient(135deg, #d4af37 0%, #b8952e 100%)"
                                                : "rgba(255, 255, 255, 0.04)",
                                            color: ativo ? "#10131a" : "#e8eef5",
                                            fontSize: "0.85rem", fontWeight: 600, whiteSpace: "nowrap",
                                            cursor: form.admin ? "not-allowed" : "pointer",
                                            WebkitTapHighlightColor: "transparent",
                                            transition: "background 0.15s, color 0.15s",
                                        }}>
                                        <i className={`${s.icone}`} style={{ color: ativo ? "#10131a" : "#d4af37" }}></i>
                                        {s.rotulo}
                                        {ativo && !form.admin && <i className="bi bi-check-lg"></i>}
                                    </button>
                                );
                            })}
                        </div>
                        <p style={{ color: "#9aa5b5", fontSize: "0.78em", margin: "8px 0 0" }}>
                            <i className="bi bi-info-circle"></i> Não marcar nenhuma seção = acesso total (o usuário vê tudo).
                            {form.admin && " Administrador sempre vê tudo — por isso os chips ficam travados."}
                        </p>
                    </div>

                    <div className="acoes-linha">
                        <button type="submit" className="btn btn-he" disabled={salvando}>
                            {salvando
                                ? <span className="spinner-border spinner-border-sm"></span>
                                : <><i className="bi bi-check-lg"></i> {editando ? "Salvar alterações" : "Criar usuário"}</>}
                        </button>
                        <button type="button" className="btn btn-outline-he" onClick={() => setForm(null)}>Cancelar</button>
                    </div>
                </form>
            }

            {carregando
                ? <div style={{ textAlign: "center", padding: "3em" }}><span className="spinner-border" style={{ color: "#d4af37" }}></span></div>
                : <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: "1em" }}>
                    {usuarios.length === 0 &&
                        <p style={{ color: "#9aa5b5", padding: "1.5em 0" }}>
                            Nenhum usuário por aqui ainda. Adicione o primeiro da equipe.
                        </p>
                    }
                    {usuarios.map((u) => {
                        const acessoTotal = u.admin || !u.permissoes || u.permissoes.length === 0;
                        const secoesDoUsuario = acessoTotal
                            ? []
                            : SECOES.filter((s) => u.permissoes.includes(s.chave));
                        const souEu = u.id === usuarioLogado.id;
                        return (
                            <div key={u.id} className="he-card sem-rolagem" style={{ flex: "1 1 300px", maxWidth: 480, minWidth: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                    <div style={{
                                        width: 42, height: 42, borderRadius: "50%", flexShrink: 0,
                                        background: "rgba(212, 175, 55, 0.15)", border: "1px solid rgba(212, 175, 55, 0.4)",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <i className="bi bi-person he-dourado" style={{ fontSize: "1.3em" }}></i>
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                            {u.nome}
                                            {u.admin &&
                                                <span style={{
                                                    background: "linear-gradient(135deg, #d4af37 0%, #b8952e 100%)",
                                                    color: "#10131a", borderRadius: 6, padding: "1px 8px",
                                                    fontSize: "0.68em", fontWeight: 700, letterSpacing: 0.6,
                                                }}>ADMIN</span>
                                            }
                                            {souEu && <span style={{ color: "#9aa5b5", fontSize: "0.75em", fontWeight: 400 }}>(você)</span>}
                                        </div>
                                        <div style={{ color: "#9aa5b5", fontSize: "0.82em" }}>
                                            <i className="bi bi-person-badge"></i> {u.login}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                    {acessoTotal
                                        ? <span style={estiloChip}><i className="bi bi-stars"></i> Acesso total</span>
                                        : secoesDoUsuario.map((s) => (
                                            <span key={s.chave} style={estiloChip}>
                                                <i className={s.icone}></i> {s.rotulo}
                                            </span>
                                        ))
                                    }
                                </div>

                                <div className="acoes-linha" style={{ marginTop: "auto" }}>
                                    <button className="btn btn-sm btn-outline-he" onClick={() => abrirEdicao(u)}>
                                        <i className="bi bi-pencil"></i> Editar
                                    </button>
                                    {!souEu &&
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleExcluir(u)}>
                                            <i className="bi bi-trash"></i> Excluir
                                        </button>
                                    }
                                </div>
                            </div>
                        );
                    })}
                </div>
            }
        </div>
    );
}
